/**
 * Carbon DataTable, Guardian's signature surface (DESIGN.md "Data table",
 * mockups 02 and 03), on layer-01:
 *   head      a 1.25rem title (also the table's accessible name) over one line
 *   notices   optional InlineNotifications about the rows, passed in
 *   tabs      ContainedTabs, passed in
 *   toolbar   search, selects, filter and icon actions (TableToolbar), the
 *             batch bar sliding over it while rows are selected, and the
 *             "Expand all" control
 *   filters   optional dismissible filter tags under the toolbar, passed in
 *   rows      a 48px layer-accent header row with sortable header buttons;
 *             body rows with a border-subtle top line, hover layer-hover,
 *             selected layer-selected, expanded (and the detail) layer-02
 *   footer    Pagination and/or a footer line (count, sort order)
 * Below c-md the header row hides and rows become stacked blocks: the
 * checkbox pinned top-left, the chevron top-right, inline cells joined by
 * " · ". Wide tables scroll inside their own container, never the page.
 *
 * Data stays with the caller: DataTable never sorts, filters or pages rows.
 * Selection is controlled (selectedIds + onSelectionChange; uncontrolled if
 * selectedIds is omitted). Row expansion is controlled the same way
 * (`expandable.expandedIds` + `onExpandedChange`), else internal and seeded by
 * `expandable.defaultExpandedIds`; group expansion is internal, seeded by
 * `groups.defaultExpanded` (`groups.flatSingles` renders a group of one as a plain row).
 */
import { useId, useMemo, useState, type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { BatchActionBar, type BatchAction } from './BatchActionBar'
import { Button } from './Button'
import { Checkbox } from './Checkbox'
import { IconArrowDown, IconArrowUp, IconChevronRight, IconSort } from './icons'
import { Pagination, type PaginationProps } from './Pagination'
import { selectionState, toggleMany } from './selection'
import { Skeleton } from './Skeleton'
import { TableToolbar } from './TableToolbar'
import { Tooltip } from './Tooltip'

export interface DataColumn<T> {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  /** The header becomes a sort button; DataTable reports the change through onSortChange. */
  sortable?: boolean
  /** Keep the cell on one line. Below c-md it renders inline at 12px, like `inlineOnPhone`. */
  nowrap?: boolean
  /** Below c-md the cell renders inline at 12px, joined to the next inline cell by " · ". */
  inlineOnPhone?: boolean
  /** Hide the column below a Carbon breakpoint. */
  hideBelow?: 'c-md' | 'c-lg' | 'c-xlg'
  /** CSS width of the column ("34%", "160px"). */
  width?: string
  align?: 'start' | 'end'
  /** The row's title cell, in primary ink (put a TableItem in it). */
  primary?: boolean
}

export interface DataTableSort {
  columnId: string
  direction: 'asc' | 'desc'
}

export interface DataTableExpandable<T> {
  render: (row: T) => ReactNode
  /** Show the "Expand all" / "Collapse all" toolbar control. */
  expandAll?: boolean
  defaultExpandedIds?: string[]
  /**
   * Controlled expansion: the expanded rows' ids (a cell title that toggles its row's detail keeps
   * this state). Omit for internal state seeded by `defaultExpandedIds`.
   */
  expandedIds?: string[]
  /** Every change to the expanded rows (chevron, row click, Expand all), controlled or not. */
  onExpandedChange?: (ids: string[]) => void
  /** Rows that have no detail get no chevron. */
  isExpandable?: (row: T) => boolean
}

export interface DataTableGroups<T> {
  key: (row: T) => string
  header: (key: string, rows: T[]) => ReactNode
  /** Groups start open. Default false. */
  defaultExpanded?: boolean
  /**
   * The group's name in its controls ("Expand Exchange", and with `selectable` the group checkbox "Select all in
   * Exchange", checked when every member is selected, mixed when some are). Default: the key.
   */
  label?: (key: string, rows: T[]) => string
  /** Show the "Expand all" / "Collapse all" toolbar control for the groups. */
  expandAll?: boolean
  /**
   * A group of one renders as a plain row, with no group header: a resource with a single drifted
   * setting stays visible while the multi-setting groups start collapsed.
   */
  flatSingles?: boolean
  /**
   * Controlled group expansion: the open groups' keys (a page that keeps them across refetches or in the URL).
   * Omit for internal state seeded by `defaultExpanded`.
   */
  expandedGroups?: string[]
  /** Every change to the open groups (a group row, Expand all), controlled or not: the open keys. */
  onExpandedGroupsChange?: (keys: string[]) => void
}

/** `selectable` as an object: which rows can be picked, and why the others can't. */
export interface DataTableSelectable<T> {
  /** False renders a disabled checkbox and keeps the row out of the page and group select-alls. Default: every row. */
  isSelectable?: (row: T) => boolean
  /** Why a row can't be picked, as a tooltip on its disabled checkbox and its accessible description. */
  disabledReason?: (row: T) => string | undefined
}

export interface DataTableProps<T> {
  columns: DataColumn<T>[]
  rows: T[]
  rowId: (row: T) => string
  /** id of the table's section, for an in-page anchor (a brief's primary action, `#scan-errors`). */
  id?: string
  /** The visible title, and the table's accessible name. */
  title?: string
  description?: ReactNode
  /** Notices between the head and the tabs (InlineNotifications about this table's rows). */
  notices?: ReactNode
  /** Accessible name when there is no visible title. */
  'aria-label'?: string
  /** ContainedTabs above the toolbar. */
  tabs?: ReactNode
  /** Toolbar contents: TableSearch, TableSelect, TableFilterMenu, IconButtons. */
  toolbar?: ReactNode
  /** The active filters as dismissible tags (`Tag onDismiss`), in a row under the toolbar. */
  filterTags?: ReactNode
  /** Row checkboxes and a select-all; an object also says which rows can be picked (`DataTableSelectable`). */
  selectable?: boolean | DataTableSelectable<T>
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  /** "Select all {total}" in the batch bar once the whole page is selected. */
  selectAllMatching?: { total: number; onSelectAll: () => void }
  batchActions?: BatchAction[]
  /** The row's name in its controls: "Select {label}", "Expand {label}". Default "row {id}". */
  rowLabel?: (row: T) => string
  expandable?: DataTableExpandable<T>
  groups?: DataTableGroups<T>
  sort?: DataTableSort | null
  /** Cycles a column ascending, descending, then unsorted (null). */
  onSortChange?: (sort: DataTableSort | null) => void
  pagination?: PaginationProps
  /** Skeleton rows in place of the body. */
  isLoading?: boolean
  /** How many skeleton rows. Default 5. */
  loadingRows?: number
  /** Row height: xs 24, sm 32, md 40, lg 48 (default). */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Pin the header row; the table scrolls inside `maxHeight` (default 24rem). */
  stickyHeader?: boolean
  maxHeight?: string
  /** Row click and Enter/Space on the focused row. */
  onRowActivate?: (row: T) => void
  /** Empty-state content. Default "Nothing to review in this view." */
  empty?: ReactNode
  /** The footer line: the shown count and the sort order. */
  footer?: ReactNode
  className?: string
}

/** The two-line item cell: a 600 title over a 12px helper sub-line ("SharePoint · Sites › Finance"). */
export function TableItem({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <>
      <b className="block font-semibold text-text-primary">{title}</b>
      {sub ? <small className="mt-0.5 block text-helper text-text-helper">{sub}</small> : null}
    </>
  )
}

const CELL_HEIGHT = { xs: 'h-6 py-0.5', sm: 'h-8 py-1', md: 'h-10 py-2', lg: 'h-12 py-3' } as const
const HEAD_HEIGHT = { xs: 'h-6', sm: 'h-8', md: 'h-10', lg: 'h-12' } as const
const HIDE = { 'c-md': 'max-c-md:hidden', 'c-lg': 'max-c-lg:hidden', 'c-xlg': 'max-c-xlg:hidden' } as const

// Phone (below c-md): every part of the table becomes a block; rows are padded stacks.
const TD_PHONE = 'max-c-md:block max-c-md:h-auto max-c-md:border-0 max-c-md:px-0 max-c-md:py-0.5'
// The " · " separator is the run's only break point between cells: a nowrap cell's ::before would inherit
// nowrap and weld the cells into one unbreakable line, so the separator wraps normally.
const TD_INLINE =
  "max-c-md:inline max-c-md:w-auto max-c-md:text-helper max-c-md:[[data-inl]+&]:before:text-text-helper max-c-md:[[data-inl]+&]:before:whitespace-normal max-c-md:[[data-inl]+&]:before:content-['_·_']"
const CONTROL_TD = 'w-12 pl-4 pr-0'
const INTERACTIVE = 'button,a,input,select,textarea,label,[role="button"]'

function isFromControl(e: MouseEvent | KeyboardEvent) {
  return e.target instanceof Element && e.target !== e.currentTarget && !!e.target.closest(INTERACTIVE)
}

function nextSort(sort: DataTableSort | null | undefined, columnId: string): DataTableSort | null {
  if (sort?.columnId !== columnId) return { columnId, direction: 'asc' }
  return sort.direction === 'asc' ? { columnId, direction: 'desc' } : null
}

function ExpandButton({ open, label, controls, onToggle }: { open: boolean; label: string; controls?: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
      onClick={onToggle}
      className="-ml-2 grid h-8 w-8 place-items-center text-[color:var(--icon-secondary)] transition-colors duration-110 ease-carbon hover:bg-layer-hover hover:text-text-primary"
    >
      <IconChevronRight className={clsx('transition-transform duration-150 ease-carbon', open && 'rotate-90')} />
    </button>
  )
}

/**
 * A row that can't be picked: a disabled checkbox. Its reason shows in a hover tooltip (a disabled control never
 * takes focus) and is the checkbox's accessible description, so a screen reader hears it too.
 */
function UnselectableBox({ label, checked, reason, reasonId }: { label: string; checked: boolean; reason?: string; reasonId: string }) {
  const box = <Checkbox aria-label={`Select ${label}`} checked={checked} disabled readOnly aria-describedby={reason ? reasonId : undefined} />
  if (!reason) return box
  return (
    <>
      <Tooltip label={reason} interactive={false} align="start">
        {box}
      </Tooltip>
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </>
  )
}

export function DataTable<T>({
  columns,
  rows,
  rowId,
  id,
  title,
  description,
  notices,
  'aria-label': ariaLabel,
  tabs,
  toolbar,
  filterTags,
  selectable,
  selectedIds,
  onSelectionChange,
  selectAllMatching,
  batchActions,
  rowLabel,
  expandable,
  groups,
  sort,
  onSortChange,
  pagination,
  isLoading,
  loadingRows = 5,
  size = 'lg',
  stickyHeader,
  maxHeight = '24rem',
  onRowActivate,
  empty,
  footer,
  className,
}: DataTableProps<T>) {
  const baseId = useId()
  const titleId = `${baseId}-title`
  const [innerSelected, setInnerSelected] = useState<string[]>([])
  const selected = selectedIds ?? innerSelected
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const setSelected = (ids: string[]) => {
    if (!selectedIds) setInnerSelected(ids)
    onSelectionChange?.(ids)
  }
  // Expansion mirrors selection: controlled by expandable.expandedIds, internal otherwise; changes always reported.
  const [innerExpanded, setInnerExpanded] = useState<string[]>(() => expandable?.defaultExpandedIds ?? [])
  const expandedList = expandable?.expandedIds ?? innerExpanded
  const expanded = useMemo(() => new Set(expandedList), [expandedList])
  const setExpanded = (ids: string[]) => {
    if (!expandable?.expandedIds) setInnerExpanded(ids)
    expandable?.onExpandedChange?.(ids)
  }
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({})

  const labelOf = (row: T) => rowLabel?.(row) ?? `row ${rowId(row)}`
  const hasExpCol = !!expandable || !!groups
  const colCount = columns.length + (hasExpCol ? 1 : 0) + (selectable ? 1 : 0)

  const grouped = useMemo(() => {
    if (!groups) return null
    const map = new Map<string, T[]>()
    for (const row of rows) {
      const k = groups.key(row)
      const list = map.get(k)
      if (list) list.push(row)
      else map.set(k, [row])
    }
    return [...map.entries()]
  }, [groups, rows])
  // Group expansion mirrors row expansion: controlled by groups.expandedGroups, internal otherwise; changes always reported.
  const groupsControlled = groups?.expandedGroups !== undefined
  const isGroupOpen = (k: string) =>
    groupsControlled ? !!groups?.expandedGroups?.includes(k) : groupOpen[k] ?? groups?.defaultExpanded ?? false
  const setGroupsOpen = (changes: Record<string, boolean>) => {
    let open: string[]
    if (groupsControlled) {
      const next = new Set(groups?.expandedGroups)
      for (const [k, on] of Object.entries(changes)) {
        if (on) next.add(k)
        else next.delete(k)
      }
      open = [...next]
    } else {
      setGroupOpen((g) => ({ ...g, ...changes }))
      open = (grouped ?? []).map(([k]) => k).filter((k) => (k in changes ? changes[k] : isGroupOpen(k)))
    }
    groups?.onExpandedGroupsChange?.(open)
  }

  // ---- selection over the rows on this page; rows the caller marks unselectable stay out of every select-all
  const pickable = typeof selectable === 'object' ? selectable : undefined
  const canSelect = (row: T) => pickable?.isSelectable?.(row) ?? true
  const pageIds = rows.filter(canSelect).map(rowId)
  const { all: allOnPage, some: someOnPage } = selectionState(selectedSet, pageIds)
  const toggleAll = () => setSelected(toggleMany(selected, pageIds))
  const toggleRow = (id: string) => setSelected(selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id])

  // ---- expand all: the groups when there are groups, otherwise every row's detail
  // With flatSingles a group of one is a plain row, so only the larger groups open and close.
  const collapsible = grouped && groups?.flatSingles ? grouped.filter(([, members]) => members.length > 1) : grouped
  const showExpandAll = !!(groups?.expandAll || expandable?.expandAll) && !(groups?.flatSingles && collapsible?.length === 0)
  const expandableIds = expandable ? rows.filter((r) => expandable.isExpandable?.(r) ?? true).map(rowId) : []
  const everythingOpen = collapsible
    ? collapsible.length > 0 && collapsible.every(([k]) => isGroupOpen(k))
    : expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id))
  const toggleEverything = () => {
    if (collapsible) setGroupsOpen(Object.fromEntries(collapsible.map(([k]) => [k, !everythingOpen])))
    else setExpanded(everythingOpen ? [] : expandableIds)
  }
  const toggleExpanded = (id: string) => setExpanded(expanded.has(id) ? expandedList.filter((x) => x !== id) : [...expandedList, id])

  const batch =
    selectable && batchActions?.length ? (
      <BatchActionBar count={selected.length} actions={batchActions} onCancel={() => setSelected([])} selectAllMatching={allOnPage ? selectAllMatching : undefined} />
    ) : null
  const showToolbar = !!toolbar || showExpandAll || (!!batch && selected.length > 0)

  const renderRow = (row: T) => {
    const id = rowId(row)
    const label = labelOf(row)
    const isSel = selectedSet.has(id)
    const canExpand = !!expandable && (expandable.isExpandable?.(row) ?? true)
    const open = canExpand && expanded.has(id)
    const detailId = `${baseId}-detail-${id}`
    const activate = onRowActivate ?? (canExpand ? () => toggleExpanded(id) : undefined)
    const out = [
      <tr
        key={id}
        data-selected={isSel || undefined}
        tabIndex={onRowActivate ? 0 : undefined}
        onClick={activate ? (e) => !isFromControl(e) && activate(row) : undefined}
        onKeyDown={
          onRowActivate
            ? (e) => {
                if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
                e.preventDefault()
                onRowActivate(row)
              }
            : undefined
        }
        className={clsx(
          'group/row transition-colors duration-110 ease-carbon',
          'max-c-md:relative max-c-md:block max-c-md:border-t max-c-md:border-line-subtle max-c-md:px-4 max-c-md:py-3',
          hasExpCol && 'max-c-md:pr-12',
          selectable && 'max-c-md:pl-12',
          activate && 'cursor-pointer',
          // Selected wins over expanded (as in the mockup); a default tag on a selected row steps up to layer-02 so it stays visible.
          isSel ? 'bg-layer-selected [&_.bg-layer-selected]:bg-layer-2' : open ? 'bg-layer-2' : 'hover:bg-layer-hover',
        )}
      >
        {hasExpCol ? (
          <td className={clsx(CONTROL_TD, 'border-t border-line-subtle max-c-md:absolute max-c-md:right-2 max-c-md:top-2 max-c-md:w-auto max-c-md:border-0 max-c-md:p-0')}>
            {canExpand ? <ExpandButton open={open} label={label} controls={open ? detailId : undefined} onToggle={() => toggleExpanded(id)} /> : null}
          </td>
        ) : null}
        {selectable ? (
          <td className={clsx(CONTROL_TD, 'border-t border-line-subtle max-c-md:absolute max-c-md:left-4 max-c-md:top-4 max-c-md:w-auto max-c-md:border-0 max-c-md:p-0')}>
            {canSelect(row) ? (
              <Checkbox aria-label={`Select ${label}`} checked={isSel} onChange={() => toggleRow(id)} />
            ) : (
              <UnselectableBox label={label} checked={isSel} reason={pickable?.disabledReason?.(row)} reasonId={`${baseId}-why-${id}`} />
            )}
          </td>
        ) : null}
        {columns.map((col) => {
          // Every hideBelow breakpoint hides the cell on phones, so it never joins the inline run there.
          const inline = (col.inlineOnPhone || col.nowrap) && !col.hideBelow
          return (
            <td
              key={col.id}
              data-inl={inline ? '' : undefined}
              className={clsx(
                'border-t border-line-subtle px-4 align-middle',
                CELL_HEIGHT[size],
                col.primary ? 'text-text-primary max-c-md:my-1' : 'text-text-secondary group-hover/row:text-text-primary',
                col.nowrap && 'whitespace-nowrap',
                col.align === 'end' && 'text-right max-c-md:text-left',
                TD_PHONE,
                inline && TD_INLINE,
                col.hideBelow && HIDE[col.hideBelow],
              )}
            >
              {col.cell(row)}
            </td>
          )
        })}
      </tr>,
    ]
    if (open && expandable) {
      out.push(
        <tr key={`${id}-detail`} id={detailId} className="bg-layer-2 max-c-md:block max-c-md:px-4 max-c-md:pb-6 max-c-md:pt-1">
          <td
            colSpan={colCount}
            className={clsx(
              'pb-8 pr-4 pt-2 text-text-secondary max-[1100px]:pl-16 max-c-md:block max-c-md:p-0',
              selectable ? 'pl-28' : 'pl-16',
            )}
          >
            {expandable.render(row)}
          </td>
        </tr>,
      )
    }
    return out
  }

  const renderGroup = ([key, members]: [string, T[]]) => {
    if (groups?.flatSingles && members.length === 1) return renderRow(members[0])
    const open = isGroupOpen(key)
    const name = groups?.label?.(key, members) ?? key
    // In a selectable table the group row carries its own select-all (every selectable member, open or collapsed).
    const memberIds = members.filter(canSelect).map(rowId)
    const memberSel = selectionState(selectedSet, memberIds)
    return [
      <tr
        key={`group-${key}`}
        onClick={(e) => !isFromControl(e) && setGroupsOpen({ [key]: !open })}
        className={clsx(
          'cursor-pointer bg-layer-1 transition-colors duration-110 ease-carbon hover:bg-layer-hover max-c-md:relative max-c-md:block max-c-md:border-t max-c-md:border-line-subtle max-c-md:px-4 max-c-md:py-3 max-c-md:pr-12',
          selectable && 'max-c-md:pl-12',
        )}
      >
        <td className={clsx(CONTROL_TD, 'border-t border-line-subtle max-c-md:absolute max-c-md:right-2 max-c-md:top-2 max-c-md:w-auto max-c-md:border-0 max-c-md:p-0')}>
          <ExpandButton open={open} label={name} onToggle={() => setGroupsOpen({ [key]: !open })} />
        </td>
        {selectable ? (
          <td className={clsx(CONTROL_TD, 'border-t border-line-subtle max-c-md:absolute max-c-md:left-4 max-c-md:top-4 max-c-md:w-auto max-c-md:border-0 max-c-md:p-0')}>
            <Checkbox
              aria-label={`Select all in ${name}`}
              checked={memberSel.all}
              indeterminate={memberSel.some}
              disabled={isLoading || memberIds.length === 0}
              onChange={() => setSelected(toggleMany(selected, memberIds))}
            />
          </td>
        ) : null}
        <td colSpan={colCount - (selectable ? 2 : 1)} className={clsx('border-t border-line-subtle px-4 font-semibold text-text-primary', CELL_HEIGHT[size], TD_PHONE)}>
          {groups?.header(key, members)}
        </td>
      </tr>,
      ...(open ? members.flatMap(renderRow) : []),
    ]
  }

  const thBase = clsx('whitespace-nowrap bg-layer-accent text-left text-body font-semibold text-text-primary', HEAD_HEIGHT[size], stickyHeader && 'sticky top-0 z-[1]')
  const scrollStyle: CSSProperties | undefined = stickyHeader ? { maxHeight } : undefined

  return (
    <section id={id} aria-labelledby={title ? titleId : undefined} className={clsx('relative min-w-0 bg-layer-1', className)}>
      {title || description ? (
        <header className="px-4 pb-6 pt-4">
          {title ? (
            <h2 id={titleId} className="text-heading font-normal text-text-primary">
              {title}
            </h2>
          ) : null}
          {description ? <p className="mt-0.5 text-body text-text-secondary">{description}</p> : null}
        </header>
      ) : null}
      {notices ? <div className={clsx('flex flex-col gap-2 px-4 pb-4', !(title || description) && 'pt-4')}>{notices}</div> : null}
      {tabs}
      {showToolbar ? (
        <TableToolbar batch={batch} covered={!!batch && selected.length > 0}>
          {toolbar}
          {showExpandAll ? (
            <Button kind="ghost" onClick={toggleEverything} className="ml-auto flex-none">
              {everythingOpen ? 'Collapse all' : 'Expand all'}
            </Button>
          ) : null}
        </TableToolbar>
      ) : null}
      {filterTags ? (
        <div role="group" aria-label="Active filters" className="flex min-w-0 flex-wrap items-center gap-2 px-4 py-3">
          {filterTags}
        </div>
      ) : null}
      <div className={clsx('min-w-0 overflow-x-auto', stickyHeader && 'overflow-y-auto')} style={scrollStyle}>
        <table
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : ariaLabel}
          aria-busy={isLoading || undefined}
          className="w-full border-collapse max-c-md:block"
        >
          <thead className="max-c-md:hidden">
            <tr>
              {hasExpCol ? (
                <th scope="col" className={clsx(thBase, CONTROL_TD)}>
                  <span className="sr-only">Expand</span>
                </th>
              ) : null}
              {selectable ? (
                <th scope="col" className={clsx(thBase, CONTROL_TD)}>
                  <Checkbox
                    aria-label="Select all rows"
                    checked={allOnPage}
                    indeterminate={someOnPage}
                    disabled={pageIds.length === 0 || isLoading}
                    onChange={toggleAll}
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const sorted = sort?.columnId === col.id ? sort.direction : null
                const SortIcon = sorted === 'asc' ? IconArrowUp : sorted === 'desc' ? IconArrowDown : IconSort
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={col.sortable ? (sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none') : undefined}
                    style={col.width ? { width: col.width } : undefined}
                    className={clsx(thBase, 'px-4', col.align === 'end' && 'text-right', col.hideBelow && HIDE[col.hideBelow])}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange?.(nextSort(sort, col.id))}
                        className={clsx(
                          '-mx-4 inline-flex items-center gap-2 px-4 font-semibold transition-colors duration-110 ease-carbon hover:bg-layer-hover',
                          HEAD_HEIGHT[size],
                        )}
                      >
                        {col.header}
                        <SortIcon className="text-[color:var(--icon-secondary)]" />
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="max-c-md:block">
            {isLoading ? (
              Array.from({ length: loadingRows }, (_, i) => (
                <tr key={`loading-${i}`} className="max-c-md:block max-c-md:border-t max-c-md:border-line-subtle max-c-md:px-4 max-c-md:py-3">
                  {hasExpCol ? <td className={clsx(CONTROL_TD, 'border-t border-line-subtle max-c-md:hidden')} /> : null}
                  {selectable ? <td className={clsx(CONTROL_TD, 'border-t border-line-subtle max-c-md:hidden')} /> : null}
                  {columns.map((col) => (
                    <td key={col.id} className={clsx('border-t border-line-subtle px-4', CELL_HEIGHT[size], TD_PHONE, col.hideBelow && HIDE[col.hideBelow])}>
                      <Skeleton width={col.primary ? '70%' : '50%'} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr className="max-c-md:block">
                <td colSpan={colCount} className="h-24 border-t border-line-subtle px-4 text-center text-text-helper max-c-md:block max-c-md:h-auto max-c-md:py-8">
                  {empty ?? 'Nothing to review in this view.'}
                </td>
              </tr>
            ) : grouped ? (
              grouped.flatMap(renderGroup)
            ) : (
              rows.flatMap(renderRow)
            )}
          </tbody>
        </table>
      </div>
      {footer ? (
        <div className="flex min-h-12 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line-subtle px-4 py-3 text-body text-text-secondary">
          {footer}
        </div>
      ) : null}
      {pagination ? <Pagination {...pagination} /> : null}
    </section>
  )
}

export default DataTable
